import {
  Box,
  Button,
  CircularProgress,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { useProgressContext } from '../components/ModulePageLayout';
import { useDiagnosticAccessGuard } from '../hooks/useDiagnosticAccessGuard';
import { useModuleTimeTracker } from '../hooks/useModuleTimeTracker';
import { courseAPI, progressAPI } from '../services/api';
import { getLearnerFacingApiError } from '../utils/learnerErrors';
import '../styles/components/_workshop.scss';
import { getLocalizedText, getDisplayLocale } from '../utils/i18n';
import { normalizeId } from '../utils/id';
import {
  ComputerIcon,
  TimeIcon,
  UsersIcon
} from '../utils/icons';
import { getNextItem, getNextItemPath } from '../utils/navigation';

interface Workshop {
  title: string | { [key: string]: string };
  description?: string | { [key: string]: string };
  image?: string;
  date?: Date | string | null;
  startTime: string;
  endTime: string;
  order: number;
  duration?: number; // Duration in minutes
  location?: string | { [key: string]: string }; // Location or "À distance"
  requirements?: string[] | { [key: string]: string[] }; // Requirements for the workshop
  objectives?: string[] | { [key: string]: string[] }; // Workshop objectives
  prerequisites?: string | { [key: string]: string }; // Prerequisites text
  virtualClassroomLink?: string; // Link to virtual classroom
  details?: string | { [key: string]: string }; // Long text field with markdown (multilingual)
  lieu?: string | { [key: string]: string }; // Simple text field (multilingual)
  distanciel?: boolean; // Boolean field
}

const Workshop: React.FC = () => {
  const { courseId, chapterId, workshopId, workshopOrder } = useParams<{
    courseId: string;
    chapterId: string;
    workshopId?: string;
    workshopOrder?: string;
  }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { refreshProgress, refreshAllProgress, refreshCourse } = useProgressContext();
  useDiagnosticAccessGuard();

  const [workshop, setWorkshop] = useState<Workshop | null>(null);
  const [workshopItemId, setWorkshopItemId] = useState<string | null>(null); // Store workshop item ID
  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null); // State for image blob URL

  // Use ref to prevent duplicate calls
  const loadingRef = React.useRef(false);

  // Track time spent on this workshop
  useModuleTimeTracker({
    courseId: courseId || '',
    chapterId: chapterId || '',
    moduleType: 'workshop',
    moduleId: workshopItemId || '',
    enabled: !!courseId && !!chapterId && !!workshopItemId && !loading
  });

  useEffect(() => {
    if (courseId && chapterId && (workshopId || workshopOrder) && !loadingRef.current) {
      loadingRef.current = true;
      loadWorkshopData().finally(() => {
        loadingRef.current = false;
      });
    }
  }, [courseId, chapterId, workshopId, workshopOrder]);

  const loadWorkshopData = async () => {
    if (!courseId || !chapterId || (!workshopId && !workshopOrder)) {
      setError(t('common.missingParameters'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Load course data
      const courseData = await courseAPI.getCourse(courseId);
      setCourse(courseData);

      // Find the workshop in the chapter (by ID if possible, else legacy by order)
      const chapter = courseData.chapters?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId));
      if (chapter) {
        let workshopItem: any = null;
        if (workshopId) {
          workshopItem = chapter.items?.find((item: any) => item.itemType === 'workshop' && normalizeId(item?._id || item?.id) === normalizeId(workshopId));
          // Legacy compatibility: old URLs used order in place of id
          if (!workshopItem && /^\d+$/.test(workshopId)) {
            const legacyOrder = parseInt(workshopId, 10);
            workshopItem = chapter.items?.find((item: any) => item.itemType === 'workshop' && item.order === legacyOrder);
          }
        } else if (workshopOrder) {
          const workshopOrderNum = parseInt(workshopOrder);
          workshopItem = chapter.items?.find((item: any) => item.itemType === 'workshop' && item.order === workshopOrderNum);
        }
        if (workshopItem) {
          console.log('🎨 Workshop item loaded:', {
            title: workshopItem.title,
            image: workshopItem.image,
            hasImage: !!workshopItem.image,
            imageUrl: workshopItem.image ? buildImageUrl(workshopItem.image) : null
          });
          setWorkshop(workshopItem);
          // Store workshop ID for time tracking
          if (normalizeId(workshopItem._id)) {
            setWorkshopItemId(normalizeId(workshopItem._id));
          }

          // If we arrived via legacy order URL, redirect to ID-based URL
          if (!workshopId && workshopOrder && normalizeId(workshopItem._id)) {
            navigate(
              `/course/${courseId}/chapter/${chapterId}/workshop/${normalizeId(workshopItem._id)}`,
              { replace: true }
            );
          }
        } else {
          setError(t('workshop.notFound'));
        }
      } else {
        setError(t('workshop.chapterNotFound'));
      }
    } catch (err: unknown) {
      console.error('Error loading workshop:', err);
      setError(getLearnerFacingApiError(err, t));
    } finally {
      setLoading(false);
    }
  };


  // Build full URL for images (similar to file download logic)
  const buildImageUrl = (imageUrl: string): string => {
    if (!imageUrl || imageUrl.trim() === '') {
      return '';
    }

    // If it's an absolute URL (http/https), use as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }

    // For relative URLs, build the full URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const baseUrl = API_BASE_URL.replace('/api', ''); // Remove /api to get server base URL

    let fullUrl: string;
    if (imageUrl.startsWith('/')) {
      // Route media paths through API so CloudFront forwards to backend.
      fullUrl = imageUrl.startsWith('/uploads/')
        ? `${API_BASE_URL}${imageUrl}`
        : `${baseUrl}${imageUrl}`;
    } else {
      // Relative URL without /, assume it's in /uploads served by API.
      fullUrl = `${API_BASE_URL}/uploads/${imageUrl}`;
    }

    console.log('🖼️ Building image URL:', {
      original: imageUrl,
      API_BASE_URL,
      baseUrl,
      fullUrl
    });

    return fullUrl;
  };

  const formatDate = (date?: Date | string | null): string => {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
      return '-';
    }
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    return dateObj.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string): string => {
    // time is in HH:mm format
    return time;
  };

  // Load image with authentication (same logic as admin ImageUpload component)
  // This hook must be called before any conditional returns
  React.useEffect(() => {
    const workshopImage = workshop?.image;
    if (workshopImage) {
      const loadImage = async () => {
        try {
          const token = localStorage.getItem('student_token');
          // Build URL same way as admin: if not full URL, prepend base URL
          const imageUrl = workshopImage.startsWith('http')
            ? workshopImage
            : buildImageUrl(workshopImage);

          console.log('🖼️ Loading workshop image:', {
            original: workshopImage,
            builtUrl: imageUrl,
            hasToken: !!token
          });

          // Fetch image with authentication (same as admin)
          const response = await fetch(imageUrl, {
            headers: token ? {
              'Authorization': `Bearer ${token}`
            } : {}
          });

          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setImageBlobUrl(url);
            console.log('✅ Image loaded successfully');
          } else {
            console.error('❌ Failed to load image:', {
              status: response.status,
              statusText: response.statusText
            });
            setImageBlobUrl(null);
          }
        } catch (error) {
          console.error('❌ Error loading image:', error);
          setImageBlobUrl(null);
        }
      };

      loadImage();

      // Cleanup blob URL on unmount or when image changes
      return () => {
        setImageBlobUrl((currentBlobUrl) => {
          if (currentBlobUrl) {
            window.URL.revokeObjectURL(currentBlobUrl);
          }
          return null;
        });
      };
    } else {
      setImageBlobUrl(null);
    }
  }, [workshop?.image]);

  // Show loading state only in the content area, not full screen
  if (loading) {
    return (
      <Box className="workshop">
        <Box className="workshop__loading">
          <CircularProgress color="inherit" />
        </Box>
      </Box>
    );
  }

  if (error || !workshop) {
    return (
      <Box className="workshop">
        <Box className="workshop__error">
          <Typography className="workshop__error-text">
            {error || t('workshop.notFound')}
          </Typography>
        </Box>
      </Box>
    );
  }

  const calculateDuration = (): number => {
    // If duration is explicitly set and is a number, use it
    if (workshop.duration && typeof workshop.duration === 'number') {
      return workshop.duration;
    }

    // Calculate duration from startTime and endTime
    if (!workshop.startTime || !workshop.endTime) {
      return 90; // Default fallback
    }

    try {
      // Ensure times are strings and in HH:mm format
      const startTimeStr = String(workshop.startTime).trim();
      const endTimeStr = String(workshop.endTime).trim();

      const startParts = startTimeStr.split(':');
      const endParts = endTimeStr.split(':');

      if (startParts.length !== 2 || endParts.length !== 2) {
        console.warn('Invalid time format:', { startTime: workshop.startTime, endTime: workshop.endTime });
        return 90; // Default fallback if format is invalid
      }

      const startHours = parseInt(startParts[0], 10);
      const startMins = parseInt(startParts[1], 10);
      const endHours = parseInt(endParts[0], 10);
      const endMins = parseInt(endParts[1], 10);

      // Validate parsed values
      if (isNaN(startHours) || isNaN(startMins) || isNaN(endHours) || isNaN(endMins)) {
        console.warn('Invalid time values:', { startHours, startMins, endHours, endMins });
        return 90;
      }

      const startTotalMinutes = startHours * 60 + startMins;
      const endTotalMinutes = endHours * 60 + endMins;

      const durationMinutes = endTotalMinutes - startTotalMinutes;

      // Return duration in minutes (ensure it's positive)
      if (durationMinutes > 0) {
        return durationMinutes;
      } else {
        console.warn('Negative duration calculated:', durationMinutes, 'from', workshop.startTime, 'to', workshop.endTime);
        return 90; // Default fallback if calculation is invalid
      }
    } catch (error) {
      console.error('Error calculating duration:', error);
      return 90; // Default fallback
    }
  };

  const getLocation = (): string => {
    // Use new 'lieu' field if available, otherwise fallback to 'location'
    if (workshop.lieu) {
      return getLocalizedText(workshop.lieu, '');
    }
    if (!workshop.location) return '';
    if (typeof workshop.location === 'string') return workshop.location;
    return getLocalizedText(workshop.location, '');
  };

  const looksLikeUrl = (value: string): boolean => {
    const v = (value || '').trim();
    if (!v) return false;
    if (/^https?:\/\//i.test(v)) return true;
    // allow "www.example.com" as a link too
    if (/^www\./i.test(v)) return true;
    return false;
  };

  const normalizeUrl = (value: string): string => {
    const v = (value || '').trim();
    if (/^https?:\/\//i.test(v)) return v;
    if (/^www\./i.test(v)) return `https://${v}`;
    return v;
  };

  const getObjectives = (): string[] => {
    if (!workshop.objectives) return [];
    if (Array.isArray(workshop.objectives)) return workshop.objectives;
    const lang = i18n.language.split('-')[0] || 'fr';
    return workshop.objectives[lang] || workshop.objectives.fr || workshop.objectives.en || workshop.objectives.pt || [];
  };

  const getPrerequisites = (): string => {
    if (!workshop.prerequisites) return '';
    if (typeof workshop.prerequisites === 'string') return workshop.prerequisites;
    return getLocalizedText(workshop.prerequisites, '');
  };

  const duration = calculateDuration();
  const location = getLocation();
  const objectives = getObjectives();
  const prerequisites = getPrerequisites();

  return (
    <Box className="workshop">
      {/* Banner Header with Image */}
      {workshop.image ? (
        <Box className="workshop__banner">
          <img
            src={imageBlobUrl || buildImageUrl(workshop.image)}
            alt={getLocalizedText(workshop.title)}
            className="workshop__banner-image"
          />
          <Box className="workshop__banner-overlay">
            <Box className="workshop__banner-top">
              <Typography className="workshop__banner-label">
                {getLocalizedText(workshop.description)}
              </Typography>
              <Box className="workshop__banner-tags">
                <Box className="workshop__banner-tag">
                  <TimeIcon size={16} className="workshop__banner-tag-icon" />
                  <span>{duration} {t('common.min')}</span>
                </Box>
                {workshop.distanciel ? (
                  <Box className="workshop__banner-tag">
                    <ComputerIcon size={16} className="workshop__banner-tag-icon" />
                    <span>{t('workshop.remote')}</span>
                  </Box>
                ) : (
                  <Box className="workshop__banner-tag">
                    <UsersIcon size={16} className="workshop__banner-tag-icon" />
                    <span>{t('workshop.inPerson')}</span>
                  </Box>
                )}
              </Box>
            </Box>
            <p className="workshop__banner-title">
              {getLocalizedText(workshop.title)}
            </p>
          </Box>
        </Box>
      ) : (
        <Box className="workshop__banner" sx={{ backgroundColor: '#2A2F2A' }}>
          <Box className="workshop__banner-overlay">
            <Box className="workshop__banner-top">
              <Typography className="workshop__banner-label">
                {getLocalizedText(workshop.description)}
              </Typography>
              <Box className="workshop__banner-tags">
                <Box className="workshop__banner-tag">
                  <TimeIcon size={16} className="workshop__banner-tag-icon" />
                  <span>{duration} {t('common.min')}</span>
                </Box>
                {workshop.distanciel ? (
                  <Box className="workshop__banner-tag">
                    <ComputerIcon size={16} className="workshop__banner-tag-icon" />
                    <span>{t('workshop.remote')}</span>
                  </Box>
                ) : (
                  <Box className="workshop__banner-tag">
                    <UsersIcon size={16} className="workshop__banner-tag-icon" />
                    <span>{t('workshop.inPerson')}</span>
                  </Box>
                )}
              </Box>
            </Box>
            <p className="workshop__banner-title">
              {getLocalizedText(workshop.title)}
            </p>
          </Box>
        </Box>
      )}

      {/* Main Content */}
      <Box className="workshop__content">
        {/* Key Information */}
        <Box className="workshop__info-section">
          <Typography className="workshop__info-item">
            <strong>{t('workshop.date')} :</strong> {formatDate(workshop.date)}
          </Typography>
          <Typography className="workshop__info-item">
            <strong>{t('workshop.time')} :</strong> {formatTime(workshop.startTime)} - {formatTime(workshop.endTime)}
          </Typography>
          {location && location.trim() && (
            <Typography className="workshop__info-item">
              <strong>{workshop.distanciel && looksLikeUrl(location) ? 'Lien visio' : 'Lieu'} :</strong>{' '}
              {looksLikeUrl(location) ? (
                <a href={normalizeUrl(location)} target="_blank" rel="noopener noreferrer">
                  {location}
                </a>
              ) : (
                location
              )}
            </Typography>
          )}
        </Box>

        {/* Details (markdown content) */}
        {workshop.details && getLocalizedText(workshop.details) && (
          <Box className="workshop__details">
            <MarkdownRenderer content={getLocalizedText(workshop.details)} />
          </Box>
        )}

        {/* Next Button */}
        {course && courseId && chapterId && (
          <Box className="workshop__next-button-container">
            <Button
              variant="contained"
              onClick={async () => {
                if (!chapterId || !courseId) return;
                const workshopOrderNum = workshop?.order || parseInt(workshopOrder || '0');
                let currentWorkshopItemId = workshopItemId || '';

                // Mark workshop as viewed/completed when clicking "Passer à la suite"
                try {
                  const workshopItem = course.chapters
                    ?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId))
                    ?.items?.find((item: any) => {
                      const id = item?._id?.toString?.() || item?._id;
                      if (workshopItemId) return item.itemType === 'workshop' && id === workshopItemId;
                      return item.itemType === 'workshop' && item.order === workshopOrderNum;
                    });
                  currentWorkshopItemId = workshopItem?._id?.toString() || currentWorkshopItemId;
                  if (currentWorkshopItemId) {
                    await progressAPI.markItemComplete(courseId, chapterId, 'workshop', currentWorkshopItemId, workshopOrderNum);
                    // Refresh course data to update item.isCompleted in timeline
                    await refreshCourse();
                    await refreshProgress();
                    await refreshAllProgress();
                  }
                } catch (error: any) {
                  console.error('❌ Error marking workshop as complete:', error?.response?.data || error?.message);
                }

                const nextItem = getNextItem(course, chapterId, currentWorkshopItemId);
                const nextPath = getNextItemPath(nextItem);
                if (nextPath) {
                  navigate(nextPath);
                } else {
                  // Fallback if no next item found (e.g., last item of last chapter)
                  navigate(`/course/${courseId}`);
                }
              }}
              className="button-primary workshop__next-button"
            >
              {t('workshop.continueButton')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Workshop;

